import { Box, Text, Button } from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import { IGatsbyImage } from 'utils/types';

// Types ---------

export interface CardProps {
  title: string;
  backgroundImage?: IGatsbyImage;
  productImage?: IGatsbyImage;
  productImageAltText?: string | null;
  cta: {
    label: string;
    url: string;
  };
  textColor?: string;
  onClickCta?: (label: string) => void;
  marginRight?: string;
  imageLoadingStrategy?: 'eager' | 'lazy';
}

// Card ---------

const Card: React.FC<CardProps> = (props) => {
  const {
    title,
    cta,
    backgroundImage,
    productImage,
    productImageAltText,
    textColor,
    onClickCta,
    marginRight,
    imageLoadingStrategy,
  } = props;

  return (
    <Box position="relative" marginRight={marginRight}>
      {/* If the background image is found, don't render product image 
      even if it exists. */}
      {!backgroundImage && productImage ? (
        <Box
          position="absolute"
          width={['150px', '224px']}
          top="-16px"
          left="50%"
          transform="translate(-50%, 0)"
          zIndex={1}
          data-testid="product-image"
          maxWidth="100%"
        >
          <GatsbyImage
            image={productImage.gatsbyImageData}
            alt={productImageAltText || ''}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
            loading={imageLoadingStrategy}
          />
        </Box>
      ) : null}

      <Box
        borderRadius="12px"
        backgroundColor="tints.cream"
        position="relative"
        overflow="hidden"
      >
        {backgroundImage ? (
          <Box
            zIndex={0}
            position="absolute"
            height="100%"
            top="0"
            left="0"
            data-testid="background-image"
          >
            <GatsbyImage
              style={{
                height: '100%',
              }}
              image={backgroundImage.gatsbyImageData}
              // This is a background image, so alt text is not needed
              alt=""
              loading={imageLoadingStrategy}
            />
          </Box>
        ) : null}

        <Box
          height={['245px', '380px']}
          width="100%"
          paddingX={['4', '6']}
          paddingY={['5', '8']}
          zIndex={1}
          position="relative"
          display="flex"
          justifyContent="center"
          flexWrap="wrap"
          alignItems="flex-end"
        >
          <Box display="flex" flexWrap="wrap" justifyContent="center">
            <Text
              size="xl"
              width="100%"
              textAlign="center"
              marginBottom={[2, 4]}
              color={textColor || 'tints.white'}
            >
              {title}
            </Text>

            {cta && cta.url ? (
              <Button
                as={Link}
                to={cta.url}
                size="sm"
                colorScheme="whiteCS"
                onClick={onClickCta ? () => onClickCta(cta.label) : undefined}
                _hover={{
                  cursor: ['default', 'pointer'],
                }}
              >
                {cta.label}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Export ---------

export default React.memo(Card);
